// ============================================================
// src/services/api.ts
// ============================================================
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

/** ---- tiny helpers to detect expired JWT (no extra deps) ---- */
function decodeJwtPayload(token: string): any | null {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const p = decodeJwtPayload(token);
  if (!p || typeof p.exp !== "number") return true;
  const now = Math.floor(Date.now() / 1000);
  return p.exp <= now;
}

const api = axios.create({ baseURL: API_BASE_URL });

/** Keep axios default header in sync with localStorage */
export const setToken = (token: string | null) => {
  if (token) {
    localStorage.setItem("token", token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem("token");
    delete api.defaults.headers.common.Authorization;
  }
};

/** Initialize default header from persisted token (if valid) */
(() => {
  const t = localStorage.getItem("token");
  if (t && !isExpired(t)) {
    api.defaults.headers.common.Authorization = `Bearer ${t}`;
  } else if (t) {
    setToken(null);
  }
})();

/** Attach Authorization for valid (non-expired) tokens only */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token && !isExpired(token)) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Handle 401 responses by logging the user out */
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem("token");
      delete api.defaults.headers.common.Authorization;

      // Only redirect if not already on auth pages
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/login') && !currentPath.startsWith('/register') && !currentPath.startsWith('/reset-password')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/* ---------- Convenience exports ---------- */
export const getAuthToken = (): string => localStorage.getItem("token") || "";

/* Example API helpers (unchanged) */
export async function getMealPlansByDateRange(start: string, end: string) {
  const res = await api.get(`/meal-plans/range`, {
    params: { start_date: start, end_date: end },
  });
  return res.data.mealPlans;
}

export async function getRecipeById(recipeId: number, mealId?: number) {
  const params = mealId ? { meal_id: mealId } : {};
  const res = await api.get(`/recipes/${recipeId}`, { params });
  return res.data.data;
}

export async function approveMealPlan(planId: number) {
  const res = await api.post(`/meal-plans/${planId}/approve`);
  return res.data.plan;
}

// Recipe interaction functions
export async function recordRecipeInteraction(recipeId: number, action: 'favorite' | 'dislike' | 'want_to_try') {
  const res = await api.post(`/recipes/${recipeId}/interaction`, { action });
  return res.data;
}

export async function getRecipeInteraction(recipeId: number) {
  const res = await api.get(`/recipes/${recipeId}/interaction`);
  return res.data.interaction;
}

export async function deleteRecipeInteraction(recipeId: number) {
  const res = await api.delete(`/recipes/${recipeId}/interaction`);
  return res.data;
}

// ─── Recipe Browse ──────────────────────────────────────────────

export interface BrowseRecipe {
  recipe_id: number;
  name: string;
  description: string | null;
  image_url: string | null;
  meal_type: string | null;
  prep_time: number | null;
  cook_time: number | null;
  total_time: number | null;
  base_servings: number | null;
  avg_rating: number;
  tags: string[];
  user_interaction: 'favorite' | 'want_to_try' | 'dislike' | null;
}

export interface BrowseResult {
  recipes: BrowseRecipe[];
  total: number;
  page: number;
  totalPages: number;
}

export async function browseRecipes(params: {
  query?: string;
  mealType?: string;
  cuisine?: string;
  page?: number;
  limit?: number;
  interaction?: 'favorite' | 'want_to_try';
  dietaryTags?: string[];
  excludeTags?: string[];
}): Promise<BrowseResult> {
  const { dietaryTags, excludeTags, ...rest } = params;
  const queryParams: Record<string, any> = { ...rest };
  if (dietaryTags && dietaryTags.length > 0) queryParams.dietaryTags = dietaryTags.join(',');
  if (excludeTags && excludeTags.length > 0) queryParams.excludeTags = excludeTags.join(',');
  const res = await api.get('/recipes/browse', { params: queryParams });
  return res.data.data;
}

export async function getRecipeCuisines(): Promise<string[]> {
  const res = await api.get('/recipes/cuisines');
  return res.data.cuisines;
}

export interface DietaryQuickTag {
  tag_name: string;
  recipe_count: number;
}

export async function getRecipeDietaryTags(): Promise<DietaryQuickTag[]> {
  const res = await api.get('/recipes/dietary-tags');
  return res.data.tags || [];
}

export async function getFamilyDietaryPreferences(): Promise<{ include: string[]; exclude: string[] }> {
  const res = await api.get('/recipes/family-preferences');
  return { include: res.data.include || [], exclude: res.data.exclude || [] };
}

export async function addRecipeToPlan(params: {
  recipe_id: number;
  meal_type: string;
  assigned_date: string;
}): Promise<{ success: boolean; meal: any; plan_id: number; servings: number }> {
  const res = await api.post('/meals/add-to-plan', params);
  return res.data;
}

// Recipe expansion functions
export async function expandRecipe(recipeId: number, servings: number = 4) {
  const res = await api.post(`/recipes/${recipeId}/expand`, { servings });
  return res.data;
}

export async function getRecipeDetails(recipeId: number) {
  const res = await api.get(`/recipes/${recipeId}/details`);
  return res.data;
}

// Meal replacement function
export async function replaceMeal(planId: number, assigned_date: string, meal_type: string, servings: number) {
  const res = await api.post(`/meal-plans/${planId}/replace-meal`, {
    assigned_date,
    meal_type,
    servings
  });
  return res.data.newRecipe;
}

// Generate recipe image on-demand
export async function generateRecipeImage(recipeId: number) {
  const res = await api.post(`/recipes/${recipeId}/generate-image`);
  return res.data;
}